'use client';

import React, { createContext, useContext, useMemo, useState } from 'react';

type ModalState = boolean;

interface ModalContextType {
  isModalOpen: ModalState;
  openModal: () => void;
  closeModal: () => void;
  toggleModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState<ModalState>(false);

  const value = useMemo(
    () => ({
      isModalOpen,
      openModal: () => setIsModalOpen(true),
      closeModal: () => setIsModalOpen(false),
      toggleModal: () => setIsModalOpen((prev) => !prev),
    }),
    [isModalOpen]
  );

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};

// Custom hook
export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used inside ModalProvider');
  }

  return context;
};
